import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react';
import api, { setAuthToken } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { isSignedIn, isLoaded: clerkLoaded, getToken } = useClerkAuth();
  const clerk = useClerk();

  const [user, setUser] = useState(null);
  const [studio, setStudio] = useState(null);
  const [dependents, setDependents] = useState([]);
  const [defaultProfileId, setDefaultProfileId] = useState(null);
  const [selectedProfileId, setSelectedProfileIdState] = useState(() => {
    try { return localStorage.getItem('dh_selected_profile_id') || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  // Capture signup role in a ref so it survives re-renders
  const signupRoleRef = useRef(sessionStorage.getItem('dancehall_signup_role'));
  const roleSentRef = useRef(false);

  // Set up auth token interceptor
  useEffect(() => {
    if (isSignedIn) {
      setAuthToken(getToken);
    }
  }, [isSignedIn, getToken]);

  // Fetch user data from our backend
  const fetchUser = useCallback(async () => {
    if (!isSignedIn) {
      setUser(null);
      setStudio(null);
      setLoading(false);
      return;
    }

    try {
      const token = await getToken();

      // Only send role param once (on first fetch after signup). The studio
      // link they signed up on rides along (stashed by the enrollment page as
      // dancehall_signup_context) so the server can record where the account
      // came from — see middleware/auth.js signupContextFrom.
      let roleParam = '';
      if (signupRoleRef.current && !roleSentRef.current) {
        const params = new URLSearchParams({ role: signupRoleRef.current });
        try {
          const ctx = JSON.parse(sessionStorage.getItem('dancehall_signup_context') || 'null');
          if (ctx?.slug) params.set('studio', ctx.slug);
          if (ctx?.courseId) params.set('course', ctx.courseId);
        } catch { /* no context stashed */ }
        roleParam = `?${params.toString()}`;
        roleSentRef.current = true;
        sessionStorage.removeItem('dancehall_signup_role');
        sessionStorage.removeItem('dancehall_signup_context');
      }

      const { data } = await api.get(`/auth/me${roleParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(data.user);
      setStudio(data.studio);
      setDependents(data.dependents || []);
      setDefaultProfileId(data.defaultProfileId || null);
    } catch (error) {
      console.error('Failed to fetch user:', error.message);
      setUser(null);
      setStudio(null);
    } finally {
      setLoading(false);
    }
  }, [isSignedIn, getToken]);

  // Set page title + favicon based on studio
  useEffect(() => {
    if (studio?.name) document.title = studio.name;
    if (studio?.branding?.logo) {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = studio.branding.logo;
      document.head.appendChild(link);
    }
  }, [studio]);

  useEffect(() => {
    if (clerkLoaded) fetchUser();
  }, [clerkLoaded, fetchUser]);

  const login = (options = {}) => {
    // Re-read signup role from sessionStorage into ref (set by signupAs before this call)
    const freshRole = sessionStorage.getItem('dancehall_signup_role');
    if (freshRole) {
      signupRoleRef.current = freshRole;
      roleSentRef.current = false;
    }

    const clerkOptions = {
      appearance: {
        variables: { colorPrimary: '#3F51B5' },
        elements: {
          footerAction: { display: 'none' }
        }
      }
    };
    if (options.studioName) {
      clerkOptions.appearance.elements.headerTitle = { display: 'none' };
      clerkOptions.appearance.elements.headerSubtitle = { display: 'none' };
      clerkOptions.localization = {
        signIn: {
          start: {
            title: `Sign in to ${options.studioName}`,
            subtitle: 'Welcome back! Please sign in to continue'
          }
        },
        signUp: {
          start: {
            title: `Sign up for ${options.studioName}`,
            subtitle: 'Create your account to get started'
          }
        }
      };
    }
    if (options.mode === 'signup') {
      clerk.openSignUp(clerkOptions);
    } else {
      clerk.openSignIn(clerkOptions);
    }
  };

  // Open Clerk's account portal modal (Security tab has password change).
  // Clerk owns all credential handling — current-password verification,
  // OAuth-only accounts, 2FA — so we never touch raw passwords ourselves.
  const openAccountSettings = () => {
    clerk.openUserProfile({
      appearance: { variables: { colorPrimary: '#3F51B5' } }
    });
  };

  const logout = () => {
    clerk.signOut();
    setUser(null);
    setStudio(null);
    setDependents([]);
    setDefaultProfileId(null);
    try { localStorage.removeItem('dh_selected_profile_id'); } catch {}
  };

  // Parental login: resolve the "currently viewing" profile.
  // List of profiles = account holder + dependents. Default to account holder
  // (backwards-compatible for all non-parent users). Selection persists in
  // localStorage so a parent's picked child survives page reloads.
  const profiles = React.useMemo(() => {
    if (!user) return [];
    return [
      { _id: user.id || user._id, name: user.name || user.email, isSelf: true },
      ...dependents.map(d => ({ _id: d._id, name: d.name, email: d.email, isSelf: false }))
    ];
  }, [user, dependents]);

  const selectedProfile = React.useMemo(() => {
    if (profiles.length === 0) return null;
    if (selectedProfileId) {
      const match = profiles.find(p => p._id?.toString() === selectedProfileId.toString());
      if (match) return match;
    }
    // No explicit pick → default to the enrolled student (server-computed in
    // /auth/me), so a parent-payer lands on their child's view, not their own
    // empty one. Falls back to the account holder.
    if (defaultProfileId) {
      const dflt = profiles.find(p => p._id?.toString() === defaultProfileId.toString());
      if (dflt) return dflt;
    }
    return profiles[0];
  }, [profiles, selectedProfileId, defaultProfileId]);

  const setSelectedProfile = useCallback((id) => {
    setSelectedProfileIdState(id);
    try {
      if (id) localStorage.setItem('dh_selected_profile_id', id.toString());
      else localStorage.removeItem('dh_selected_profile_id');
    } catch {}
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      studio,
      dependents,
      profiles,
      selectedProfile,
      setSelectedProfile,
      isAuthenticated: !!isSignedIn,
      loading: !clerkLoaded || loading,
      login,
      logout,
      openAccountSettings,
      refreshUser: fetchUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
